import { Component, ViewEncapsulation, ViewChild, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { fadeInOut } from '../../shared/helpers/animations';
import { AuthorizationService, OrderService, SettingsService, StorageManagerService } from '../../shared/services/index';
import { LCRFormService } from '../../shared/services/lcr-form.service';
import { GridComponent, SortService, FilterService, SearchService } from '@syncfusion/ej2-angular-grids';
import { IDashboardRequest, IPaginatedResult, createDefaultDashboardRequest } from '../../shared/models/index';
// import { NgbTabset } from '@ng-bootstrap/ng-bootstrap';
import { NgbNav } from '@ng-bootstrap/ng-bootstrap';

import { ToastrService } from 'ngx-toastr';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription, interval } from 'rxjs';
import { TooltipComponent } from '@syncfusion/ej2-angular-popups';
import { DeleteButtonComponent } from '../common/delete-button/delete-button.component';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations/src';
import { Utilities } from '../../shared/services/utilities';
import { EsigModalComponent } from '../common/esig-modal/esig-modal.component';
import { PragmaticValidationService } from '../../shared/services/pragmatic-validation.service';
import { PragmaticValidationPollService } from '../../shared/services/pragmatic-validation-poll.service';
import { RTCDPragmaticValidation } from '../../shared/models/rtcd_pragmatic_validation.model';
import { FormType } from '../../shared/models/form-type';

@Component({
  selector: 'app-lcr-form-dashboard',
  templateUrl: './lcr-form-dashboard.component.html',
  styleUrls: ['./lcr-form-dashboard.component.scss'],
  providers: [SortService, FilterService, SearchService],
  animations: [fadeInOut],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false
})
export class LCRFormDashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('LCRForm_grid')
  public LCRForm_grid: GridComponent;
  @ViewChild('deliveries_grid')
  public deliverieGrid: GridComponent;
  @ViewChild('ngbNav', { static: true })
  public tabset: NgbNav;
  @ViewChild('tooltip')
  public tooltip: TooltipComponent;
  @ViewChild('deleteLCRButton')
  public deleteButton: DeleteButtonComponent;
  @ViewChild('deleteLCRDeliveryButton')
  public deleteButtonDelivery: DeleteButtonComponent;
  @ViewChild('sendToFDAButton')
  public sendToFDAButton: EsigModalComponent;
  // Captured from the row data; tells the delete button whether the selected
  // row is a draft (so it deactivates only drafts) or a published version.
  selectedLCRRevisionDraftMode: boolean | undefined;
  selectedLCRRevisionProductType: string | undefined;
  selectedLCRRevisionFileName: string | undefined;
  selectedLCRRevisionVersion: string | undefined;

  // Initialize with empty server-side format to prevent Syncfusion template errors before data loads
  LCRForm_grid_data: any = { result: [], count: 0 };
  LCRDeliveries_grid_data: any = { result: [], count: 0 };
  public initialPage: Object;
  // Separate page settings for each grid to track server-side totals
  public lcrFormsPageSettings: Object;
  public deliveriesPageSettings: Object;
  public formatOptions: object;
  public toolbarOptions: Object;
  count_LCRForm: number = 0;
  count_deliveriesLCRForm: number = 0;
  loading = false;
  loadingLCRForms = true;
  submitted = false;

  // Server-side pagination state for LCR forms (drafts + majors combined)
  lcrFormsRequest: IDashboardRequest;
  // Server-side pagination state for deliveries
  deliveriesRequest: IDashboardRequest;
  loadingDeliveries = true;

  /**
   * Explicit selection persistence across paging/sorting/filtering.
   * Syncfusion persistSelection can still drop selection depending on templates/checkbox visibility,
   * so we keep the selected primary key and re-apply it on actionComplete.
   */
  private selectedLCRFormRevisionId: number | null = null;
  public searchForm: UntypedFormGroup;
  errorMessage: string;
  bHasPermission = false;
  sub: Subscription;
  formData: any;
  activeTab: string;
  public clipmodecolumn = { class: 'e-tooltipcolumn' };
  hasDeletePermission = false;
  selectedLcrRevisionId: number | undefined;

  needsToPoll: boolean = true;

  // While a pragmatic validation is running we poll the deliveries dashboard and HOLD
  // the native grid spinner until the selected task reaches a terminal state
  // (pragmaticTaskStatusCode >= 3: Complete=3 or Error=4). This way the "In process"
  // row is never shown with the spinner already gone.
  private validationPollSub?: Subscription;

  constructor(private router: Router, private orderService: OrderService, private toastr: ToastrService, private storageManagerService: StorageManagerService,
    private fb: UntypedFormBuilder, private authorizationService: AuthorizationService, private activatedRoute: ActivatedRoute, private sanitizer: DomSanitizer,
    private pragmaticValidationService: PragmaticValidationService,
    private pragmaticValidationPollService: PragmaticValidationPollService,
    private settingsService: SettingsService,
    private lcrFormService: LCRFormService,
  ) {
  }

  ngAfterViewInit(): void {
    this.syncGridSpinners();
  }

  ngOnInit(): void {

    this.searchForm = this.fb.group({
      searchText: ['', { validators: [Validators.required], updateOn: 'blur' }]
    });

    // Check if user has write permission. If no, disable the "Save", "Save a draft" buttons
    this.checkUserPermission();

    this.toolbarOptions = this.createCustomToolbarItems(this.hasDeletePermission, this.bHasPermission);

    this.initialPage = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100] };
    // Initialize separate page settings for each grid
    this.lcrFormsPageSettings = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100] };
    this.deliveriesPageSettings = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100] };
    this.formatOptions = { type: 'dateTime', format: 'MM/dd/yyyy hh:mm a' };

    // Initialize server-side pagination requests. The LCR grid now shows the
    // latest revision per LCR (draft or published) on a single tab; the SP
    // ignores isDraftMode but the field is required by IDashboardRequest.
    this.lcrFormsRequest = createDefaultDashboardRequest(false);
    this.lcrFormsRequest.pageSize = 25;
    this.lcrFormsRequest.sortColumn = 'formatedModifiedDate';
    this.lcrFormsRequest.sortDirection = 'DESC';
    this.deliveriesRequest = createDefaultDashboardRequest(false);
    this.deliveriesRequest.pageSize = 25;

    this.pollData();

    this.activeTab = 'LCRFormGrid';
    // set focus on the invisible div so the first tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
    this.initTabIndex();

    // Load LCR forms (drafts + majors combined) with server-side pagination
    this.loadLCRForms();
  }

  pollData() {
    this.loadDeliveries(); // Start polling immediately
    // Poll every minute (60000 milliseconds) only if needsToPoll = true.
    // Skip while a validation status poll is active so the held spinner isn't wiped
    // by a background rebind.
    this.sub = interval(60000).subscribe(() => {
      if (this.needsToPoll && !this.validationPollSub) {
        this.loadDeliveries();
      }
    });
  }

  // Load deliveries with server-side pagination
  loadDeliveries() {
    this.loadingDeliveries = true;
    this.setGridLoading(this.deliverieGrid, true);
    this.lcrFormService.getLCRDeliveryDashboardPaginated(this.deliveriesRequest).subscribe({
      next: (response: any) => {
        const result = response?.data;
        const pageNumber = result?.pageNumber ?? 1;
        const pageSize = result?.pageSize ?? 25;
        const totalRecords = result?.totalRecords ?? 0;
        const data = (result?.data ?? []).map(x => {
          if (x?.formatedModifiedDate) {
            x.formatedModifiedDate = new Date(x.formatedModifiedDate as any);
          }
          return x;
        });

        const processedData = data.map(x => {
          if (x.modifiedOn) {
            x.modifiedOn = new Date(x.modifiedOn as any);
          }
          if (x.pragmaticValidationDate) {
            x.pragmaticValidationDate = new Date(x.pragmaticValidationDate);
          }
          return x;
        });

        this.count_deliveriesLCRForm = totalRecords;
        this.LCRDeliveries_grid_data = { result: processedData, count: totalRecords };

        this.updateDeliveriesNeedsToPoll(processedData);
        this.loadingDeliveries = false;
        this.setGridLoading(this.deliverieGrid, false);
      },
      error: () => {
        this.loadingDeliveries = false;
        this.setGridLoading(this.deliverieGrid, false);
      }
    });
  }

  private updateDeliveriesNeedsToPoll(data: any[]): void {
    const today: Date = new Date();
    const numberOfDaysAgo: Date = new Date(today.getTime() - parseInt(this.settingsService.pragmaticValidatorAutoRefreshDays, 10) * 24 * 60 * 60 * 1000);
    const filteredDataPragmaticTaskStatusInProgress = data.filter(item => item.pragmaticTaskStatusCode === 1);
    const filteredDataWithConfigDaysAgo = filteredDataPragmaticTaskStatusInProgress.some(item => {
      const createDate: Date = new Date(item.pragmaticValidationDate);
      return createDate >= numberOfDaysAgo;
    });
    if (!filteredDataWithConfigDaysAgo) {
      this.needsToPoll = false;
    }
  }

  pragmaticValidation() {
    const currentData = this.LCRDeliveries_grid_data.result as any[];
    const foundItemIndex = currentData.findIndex(x => x.fileName === this.selectedLCRRevisionFileName);
    if (foundItemIndex === -1) {
      return;
    }
    const currentStatusCode = currentData[foundItemIndex].pragmaticTaskStatusCode;
    // 1 = Pending, 2 = InProgress (both non-terminal): already running.
    if (currentStatusCode === 1 || currentStatusCode === 2) {
      this.toastr.warning(`Pragmatic validation is already in process.`, '', { closeButton: true });
      return;
    }

    const selectedFileName = currentData[foundItemIndex].fileName;
    // Snapshot the row's CURRENT completion timestamp before triggering a new run.
    // Validate returns 200 immediately, but the fire-and-forget backend task may not
    // have flipped the status to In-Process yet. Without this snapshot the first poll
    // can read the PREVIOUS run's terminal status and close the spinner early.
    const initialValidationDateMs = this.toMs(currentData[foundItemIndex].pragmaticValidationDate);
    const customerGuid = this.settingsService.getCustomerGuid();
    let pragmaticData: RTCDPragmaticValidation = {
      formRevisionId: currentData[foundItemIndex].formRevisionID,
      sourceFilePath: currentData[foundItemIndex].filePath,
      sourceFileName: currentData[foundItemIndex].fileName,
      fileType: FormType.LCR,
      createUser: this.storageManagerService.read('userData').userguid,
      changeUser: this.storageManagerService.read('userData').userguid,
      customerGuid,
    }

    // Show the grid's built-in (native) Syncfusion spinner and HOLD it until the
    // validation reaches a terminal status. We intentionally do NOT hide it when the
    // Validate call returns: that endpoint returns 200 immediately while processing
    // continues in the background. waitForValidationToComplete() keeps the spinner up
    // and closes it once the status settles to Complete/Error (or the safety timeout),
    // so the "In process" row is never shown with the spinner already gone.
    this.setGridLoading(this.deliverieGrid, true);
    this.pragmaticValidationService.pragmaticValidation(pragmaticData)
      .subscribe({
        next: () => {
          this.needsToPoll = true;
          this.waitForValidationToComplete(selectedFileName, initialValidationDateMs);
        },
        error: (error) => {
          // The Validate request itself failed, so nothing is running; release the spinner.
          this.setGridLoading(this.deliverieGrid, false);
          this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
        },
      });
  }

  // Poll the deliveries dashboard WITHOUT rebinding the grid (so the native spinner
  // survives) until the selected file's pragmatic task reaches a FRESH terminal state
  // (pragmaticTaskStatusCode >= 3 => Complete/Error) or the safety timeout elapses.
  // A terminal status only counts once it reflects the NEW run (we either saw it go
  // In-Process, or its completion date is newer than the snapshot) so a stale terminal
  // read on the first poll can't close the spinner before processing starts.
  private waitForValidationToComplete(fileName: string, initialValidationDateMs: number): void {
    this.stopValidationPolling();

    this.validationPollSub = this.pragmaticValidationPollService.poll<any>({
      poll: () => this.lcrFormService.getLCRDeliveryDashboardPaginated(this.deliveriesRequest),
      extractRows: (response) => (response?.data?.data ?? []) as any[],
      matchRow: (row) => row.fileName === fileName,
      initialValidationDateMs,
      // Single rebind: refreshes the row to its final status AND hides the spinner.
      onFreshTerminal: () => this.loadDeliveries(),
      releaseSpinner: () => this.setGridLoading(this.deliverieGrid, false),
      onTimeout: () => this.toastr.info('Validation is taking longer than expected; it will continue in the background.'),
    });

    // Clear the handle when the stream ends on its own (terminal/timeout) so the
    // background pollData() interval, which is gated on !this.validationPollSub,
    // resumes refreshing the deliveries grid.
    this.validationPollSub.add(() => {
      this.validationPollSub = undefined;
    });
  }

  private stopValidationPolling(): void {
    this.validationPollSub?.unsubscribe();
    this.validationPollSub = undefined;
  }

  /** Safe epoch-ms conversion for a value that may be a Date, ISO string, or null. */
  private toMs(value: any): number {
    if (!value) {
      return 0;
    }
    const ms = new Date(value).getTime();
    return isNaN(ms) ? 0 : ms;
  }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById("firstDiv");

    if (main) {
      main.focus();
    }
  }

  tabChanged(event) {
    this.activeTab = event.nextId;
    // Setup the tool bar configuration
    this.lcrFormGridRowDeselected();
    this.toolbarOptions = this.createCustomToolbarItems(this.hasDeletePermission, this.bHasPermission);
    this.selectedLcrRevisionId = undefined;

    // Restore selection when switching tabs
    setTimeout(() => this.restoreSelectionForActiveTab());
    setTimeout(() => this.syncGridSpinners());
  }

  getActiveGrid() {
    let grid;

    switch (this.activeTab) {
      case 'deliveries':
        grid = this.deliverieGrid;
        break;
      case 'LCRFormGrid':
        grid = this.LCRForm_grid;
        break;
    }

    return grid;
  }

  tooltipValue(args) {
    if (args.target.scrollWidth <= args.target.clientWidth) {
      args.cancel = true;
    } else {

      const grid = this.getActiveGrid();
      const rowInfo = grid?.getRowInfo(args.target);
      const rowData = rowInfo.rowData;
      this.tooltip.cssClass = 'customTooltip';
      this.tooltip.content = rowData[(rowInfo.column as any).field];
    }
  }

  // Handle actionBegin - change filter operator from "startswith" to "contains"
  // and handle filtering server-side
  public begin(args: any): void {
    if (args.requestType === 'filtering') {
      // Change operator from "startswith" to "contains" for text columns
      if (args.columns) {
        args.columns.forEach(col => {
          if (col.field !== 'modified') {
            col.operator = 'contains';
          }
        });
      }
      // Don't cancel - let dataStateChange handle the actual filtering
    }
  }

  // Display detail of a single LCR form
  viewLCRForm(lcrRevisionId: string) {
    if (lcrRevisionId) {
      this.router.navigate(['/lcr-form', lcrRevisionId]);
    }
  }

  // Download spl files
  downloadFile(filePath: string, fileName: string) {

    this.sub = this.orderService.downloadFile(filePath, fileName).subscribe(response => {
      // non-IE
      const URL = window.URL || (<any>window).webkitURL;
      const downloadUrl = URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.click();
    },
      error => {
        this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
      }
    );
  }

  // Check if user has write permission. If no, disable the "Save", "Save a draft" buttons
  checkUserPermission() {
    if (this.authorizationService.hasPermission('SSDP.CanWriteLCRForm')) {
      this.bHasPermission = true;
    }
    if (this.authorizationService.hasPermission('SSDP.CanDeleteLCRForm')) {
      this.hasDeletePermission = true;
    }
  }

  createCustomToolbarItems(deletePermission: boolean, writePermission: boolean) {
    let toolbarItems = [];

    if (this.activeTab === 'deliveries' && writePermission) {
      toolbarItems.push(
        {
          text: 'Send to FDA',
          tooltipText: 'Send to FDA',
          prefixIcon: 'fas fa-paper-plane',
          id: 'tool-bar-send'
        }
      );
    }

    if (writePermission) {
      toolbarItems.push(
        {
          text: 'Create New LCR',
          tooltipText: 'Add a new LCR to this list',
          prefixIcon: 'fas fa-plus',
          id: 'create-form-btn'
        }
      );
    }

    if (deletePermission) {
      toolbarItems.push(
        {
          text: 'Delete LCR',
          tooltipText: 'Delete the selected LCR',
          prefixIcon: 'fas fa-trash',
          id: 'tool-bar-delete'
        }
      )
    }

    if (this.activeTab === 'deliveries' && writePermission) {
      toolbarItems.push(
        {
          text: 'Pragmatic',
          tooltipText: 'Validate the file with Pragmatic',
          prefixIcon: 'fal fa-circle-parking',
          id: 'validation-form-btn'
        }
      );
    }

    toolbarItems.push('Search');

    return toolbarItems;
  }

  onSubmitionToFDA(isSuccessfulySubmitted: boolean) {
    if (isSuccessfulySubmitted) {
      if (this.deliverieGrid) {
        this.deliverieGrid.clearSelection();
      }
    }
  }

  // Grid toolbar click event handler
  toolbarClick(args: ClickEventArgs): void {

    if (args.item.id === 'create-form-btn' && this.bHasPermission) {
      this.router.navigate(["/lcr-form"]);
    }
    else if (!this.selectedLcrRevisionId &&
      (args.item.id === 'tool-bar-delete' || args.item.id === 'tool-bar-send' || args.item.id === 'validation-form-btn')) {
      this.toastr.warning(`No Row(s) selected.`, '', { closeButton: true });
      return;
    }
    else if (args.item.id === 'tool-bar-delete' && this.selectedLcrRevisionId) {
      // Pick the delete handler based on the active tab. The unified LCR grid
      // contains both drafts and majors; the delete-button reads
      // `selectedLCRRevisionDraftMode` to decide which IsDraftMode bucket to deactivate.
      if (this.activeTab === 'deliveries') {
        this.deleteButtonDelivery.revisionId = this.selectedLcrRevisionId.toString();
        this.deleteButtonDelivery.openDeleteModal();
      }
      else {
        this.deleteButton.revisionId = this.selectedLcrRevisionId.toString();
        this.deleteButton.openDeleteModal();
      }
    }
    else if (this.deliverieGrid && this.selectedLCRRevisionFileName && args.item.id === 'tool-bar-send') {
      this.sendToFDAButton.showEsignatureModal();
    } else if (this.deliverieGrid && this.selectedLCRRevisionFileName && args.item.id === 'validation-form-btn') {
      this.pragmaticValidation()
    }
  }

  ngOnDestroy(): void {

    if (this.sub) {
      this.sub.unsubscribe();
    }
    this.stopValidationPolling();
  }
  // convenience getter for easy access to form fields
  get f() { return this.searchForm.controls; }

  lcrFormGridRowSelected(e: any) {
    const id = e?.data?.lcrRevisionId;
    this.selectedLcrRevisionId = id;

    // Capture the row-level draft flag so the delete button targets only the
    // matching IsDraftMode bucket (mirrors the LL Product Portfolio pattern).
    this.selectedLCRRevisionDraftMode = e?.data?.isDraftMode;
    this.selectedLCRRevisionProductType = e.data.documentType;
    this.selectedLCRRevisionFileName = e.data.fileName;
    this.selectedLCRRevisionVersion = e.data.formVersion;

    // Track selection for persistence (only the LCR grid persists across paging/sorting)
    if (this.activeTab === 'LCRFormGrid') {
      this.selectedLCRFormRevisionId = id;
    }
  }

  lcrFormGridRowDeselected() {
    this.selectedLcrRevisionId = undefined;

    this.selectedLCRRevisionDraftMode = undefined;
    this.selectedLCRRevisionProductType = undefined;
    this.selectedLCRRevisionFileName = undefined;
    this.selectedLCRRevisionVersion = undefined;

    if (this.activeTab === 'LCRFormGrid') {
      this.selectedLCRFormRevisionId = null;
    }
  }

  // Load LCR forms with server-side pagination
  loadLCRForms() {
    this.loadingLCRForms = true;
    this.setGridLoading(this.LCRForm_grid, true);
    this.lcrFormService.getLCRFormsPaginated(this.lcrFormsRequest).subscribe(
      (response: any) => {
        // API returns { isSuccess, message, data: IPaginatedResult }
        // Unwrap to get the paginated result
        const result = response?.data;

        // Ensure result has required properties with safe defaults
        const pageNumber = result?.pageNumber ?? 1;
        const pageSize = result?.pageSize ?? 25;
        const totalRecords = result?.totalRecords ?? 0;
        const data = (result?.data ?? []).map(x => {
          if (x?.formatedModifiedDate) {
            x.formatedModifiedDate = new Date(x.formatedModifiedDate as any);
          }
          return x;
        });

        // Update count
        this.count_LCRForm = totalRecords;

        this.LCRForm_grid_data = { result: data, count: totalRecords };
        this.loadingLCRForms = false;
        this.setGridLoading(this.LCRForm_grid, false);
      },
      error => {
        this.loadingLCRForms = false;
        this.setGridLoading(this.LCRForm_grid, false);
        this.toastr.error(error.message || 'Failed to load LCR forms');
      }
    );
  }

  private syncGridSpinners(): void {
    this.setGridLoading(this.LCRForm_grid, this.loadingLCRForms);
    this.setGridLoading(this.deliverieGrid, this.loadingDeliveries);
  }

  private setGridLoading(grid: GridComponent | undefined, isLoading: boolean): void {
    setTimeout(() => {
      if (!grid) {
        return;
      }

      if (isLoading) {
        grid.showSpinner();
      } else {
        grid.hideSpinner();
      }
    });
  }

  // Handle LCR forms grid data state changes (paging, sorting, searching, filtering)
  onLCRFormsDataStateChange(state: any): void {
    // Skip initial data load (handled by ngOnInit) - no action or refresh without search
    if (!state.action || (state.action.requestType === 'refresh' && !state.action.searchString)) {
      return;
    }
    this.updateRequestFromState(this.lcrFormsRequest, state);
    this.loadLCRForms();
  }

  // Update the pagination request based on Syncfusion grid state (paging, sorting, searching, filtering)
  private updateRequestFromState(request: IDashboardRequest, state: any): void {
    // Handle paging - use skip and take from dataStateChange
    if (state.skip !== undefined && state.take !== undefined) {
      request.pageNumber = Math.floor(state.skip / state.take) + 1;
      request.pageSize = state.take;
    }

    // Handle sorting
    if (state.sorted && state.sorted.length > 0) {
      const sortInfo = state.sorted[0];
      request.sortColumn = sortInfo.name;
      request.sortDirection = sortInfo.direction === 'descending' ? 'DESC' : 'ASC';
    } else if (state.action && state.action.requestType === 'sorting') {
      // Also check action for sorting info
      if (state.action.columnName) {
        request.sortColumn = state.action.columnName;
        request.sortDirection = state.action.direction === 'Descending' ? 'DESC' : 'ASC';
      } else {
        request.sortColumn = '';
        request.sortDirection = 'ASC';
      }
    }

    // Handle searching from dataStateChange
    if (state.search && state.search.length > 0) {
      request.searchTerm = state.search[0].key || '';
      request.pageNumber = 1; // Reset to first page on new search
    } else if (state.action && state.action.requestType === 'searching') {
      // Handle searching from action event
      request.searchTerm = state.action.searchString || '';
      request.pageNumber = 1; // Reset to first page on new search
    } else if (!state.search || state.search.length === 0) {
      // Clear search term when no search is active
      if (request.searchTerm) {
        request.searchTerm = '';
        request.pageNumber = 1;
      }
    }

    // Handle filtering from dataStateChange - rebuild filters from state.where
    if (state.action && (state.action.requestType === 'filtering' || state.action.requestType === 'clearfiltering')) {
      const newFilters: { [key: string]: string } = {};

      if (state.where && state.where.length > 0) {
        state.where.forEach(condition => {
          if (condition.predicates && condition.predicates.length > 0) {
            condition.predicates.forEach(pred => {
              if (pred.field && pred.value !== undefined && pred.value !== null && pred.value !== '') {
                newFilters[pred.field] = this.formatColumnFilterValue(pred.field, pred.value);
              }
            });
          } else if (condition.field && condition.value !== undefined && condition.value !== null && condition.value !== '') {
            newFilters[condition.field] = this.formatColumnFilterValue(condition.field, condition.value);
          }
        });
      }

      request.columnFilters = newFilters;
      request.pageNumber = 1;
    }
  }

  private formatColumnFilterValue(field: string, value: any): string {
    if (field === 'formVersion') {
      const version = String(value).trim();
      return /^\d+$/.test(version) ? `${version}.0` : version;
    }

    return String(value);
  }

  /** Called by the grids after paging/sorting/filtering so we can reselect the row. */
  gridActionComplete(args: any) {
    switch (args?.requestType) {
      case 'paging':
      case 'sorting':
      case 'filtering':
      case 'searching':
      case 'refresh':
        this.restoreSelectionForActiveTab();
        break;
    }
  }

  private restoreSelectionForActiveTab() {
    if (this.activeTab !== 'LCRFormGrid') {
      return;
    }

    const grid: GridComponent = this.LCRForm_grid;
    const id: number | null = this.selectedLCRFormRevisionId;

    if (!grid || !id) {
      return;
    }

    const view = grid.getCurrentViewRecords() as any[];

    let rowIndex = -1;
    if (view && view.length) {
      for (let i = 0; i < view.length; i++) {
        if (view[i] && view[i].lcrRevisionId === id) {
          rowIndex = i;
          break;
        }
      }
    }

    if (rowIndex >= 0) {
      grid.selectRow(rowIndex);
    }
  }

  // Handle deliveries grid data state changes (paging, sorting, searching, filtering)
  onDeliveriesDataStateChange(state: any): void {
    if (!state.action || (state.action.requestType === 'refresh' && !state.action.searchString)) {
      return;
    }
    this.updateRequestFromState(this.deliveriesRequest, state);
    this.loadDeliveries();
  }

}
